import React, { useMemo } from "react";
import { Button, DownloadIcon, Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { useLicensesGoBack } from "~/entity/Licenses";
import {
  LICENSES_POSITION_SORT_TO_API,
  mapLicensesSortToQuery,
  mapLicenseTypeToApi,
} from "~/entity/Licenses/model/licenseQueryParams";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useNotify } from "~/shared/hooks/useNotify";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { getAvailabilityColor } from "~/shared/lib/licenseAvailability";
import { TableV2, useURLTableSort } from "~/shared/ui/TableV2";

import {
  buildLicenseOrganizationColumns,
  LICENSE_ORGANIZATION_SORTABLE_COLUMNS,
} from "./model/licenseOrganizationColumns";
import {
  ILicenseOrganizationRow,
  ILicenseOrganizationUrlParams,
  LICENSE_ORGANIZATION_TOTALS_ROW_ID,
} from "./model/types";
import { useOrganizationLicenses } from "./model/useOrganizationLicenses";

import s from "./LicenseOrganization.less";

const LicenseOrganization = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const { handleCount, handlePage } = usePaginationParams("urlParams");
  const goBack = useLicensesGoBack();
  const urlParams = useURLParamsGetter<ILicenseOrganizationUrlParams>();
  const {
    dealerId = "",
    organizationId = "",
    organizationName = "",
    licenseType = "",
    page,
    count = 25,
    sortField,
    sortType: sortTypeRaw,
  } = urlParams;

  const { notifyWarning } = useNotify();

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns: LICENSE_ORGANIZATION_SORTABLE_COLUMNS,
    setParams,
  });

  const tableSortField =
    sortField &&
    LICENSE_ORGANIZATION_SORTABLE_COLUMNS.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  const queryParams = useMemo(
    () => ({
      dealerId: dealerId ? Number(dealerId) : undefined,
      organizationId: organizationId ? String(organizationId) : undefined,
      licenseType: mapLicenseTypeToApi(String(licenseType)),
      page: pageNumber,
      count: pageSize,
      ...mapLicensesSortToQuery(
        tableSortField,
        normalizedSortType,
        LICENSES_POSITION_SORT_TO_API,
      ),
    }),
    [
      dealerId,
      organizationId,
      licenseType,
      pageNumber,
      pageSize,
      tableSortField,
      normalizedSortType,
    ],
  );

  const { rows, total, statusTotals, isLoading } =
    useOrganizationLicenses(queryParams);

  const columns = useMemo(
    () =>
      buildLicenseOrganizationColumns(
        t,
        tableSortField,
        normalizedSortType,
        String(licenseType),
      ),
    [t, tableSortField, normalizedSortType, licenseType],
  );

  const totalsRow = useMemo<ILicenseOrganizationRow | null>(() => {
    if (!statusTotals) {
      return null;
    }

    return {
      id: LICENSE_ORGANIZATION_TOTALS_ROW_ID,
      instanceId: null,
      positionCode: "",
      positionName: "",
      availabilityColor: getAvailabilityColor(
        statusTotals.available,
        statusTotals.total,
      ),
      totalQuantity: statusTotals.total,
      assignedQuantity: statusTotals.assigned,
      usedQuantity: statusTotals.used,
      frozenQuantity: statusTotals.frozen,
      freeQuantity: statusTotals.available,
      expiredQuantity: statusTotals.expired,
    };
  }, [statusTotals]);

  const tableRows = useMemo(
    () => (totalsRow ? [totalsRow, ...rows] : rows),
    [totalsRow, rows],
  );

  const handleExportExcel = () => {
    notifyWarning(t("licenseOrganization.notification.excelUnavailable"));
  };

  const openPositionGroup = (row: ILicenseOrganizationRow) => {
    const params = new URLSearchParams();

    if (row.instanceId != null) {
      params.set("instanceId", String(row.instanceId));
    }

    params.set("positionName", row.positionName);

    if (dealerId) {
      params.set("dealerId", String(dealerId));
    }

    if (organizationId) {
      params.set("organizationId", String(organizationId));
    }

    params.set("organizationName", String(organizationName));

    if (licenseType) {
      params.set("licenseType", String(licenseType));
    }

    window.open(
      `/staff/licenses/position-group?${params.toString()}`,
      "_blank",
    );
  };

  return (
    <Wrapper.Content
      title={t("licenseOrganization.title", { organizationName })}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
    >
      <div className={s.downloadToolbar}>
        <Button
          bold
          color="secondary"
          variant="filled"
          fontSize="10"
          size="small"
          radius={30}
          icon={<DownloadIcon size={12} />}
          onClick={handleExportExcel}
        >
          {t("licenseOrganization.downloadExcel")}
        </Button>
      </div>

      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={tableRows}
        rowKey="id"
        loading={isLoading}
        noHorizontalScroll
        sortableColumns={LICENSE_ORGANIZATION_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        rowDecorator={(record) =>
          record.id === LICENSE_ORGANIZATION_TOTALS_ROW_ID
            ? { className: s.totalsRow }
            : {}
        }
        onRow={(record) =>
          record.id === LICENSE_ORGANIZATION_TOTALS_ROW_ID
            ? {}
            : {
                onClick: () => openPositionGroup(record),
                style: { cursor: "pointer" },
              }
        }
      />

      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
        selectFrom={[25, 50, 100]}
      />
    </Wrapper.Content>
  );
};

export default LicenseOrganization;
